import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";

// ─── Helpers (same as list page) ──────────────────────────────────────────────
const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const isShipDatePassed = (shipDate) => {
  if (!shipDate) return false;
  const ship = new Date(shipDate);
  const today = new Date();
  ship.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return ship < today;
};

const getPartialInfo = (invoice) => {
  if (invoice?.invoiceType !== "partial") return { isPartial: false };

  const partialPaid = !!invoice?.partialPaid;
  const partialMode = invoice?.partialMode || "percentage";
  const partialValue = Number(invoice?.partialValue ?? 0);
  const totalAmount = Number(
    invoice?.totalAmount ?? invoice?.totalAmountDue ?? 0,
  );
  const minimumPartialAmt = Number(invoice?.minimumPartialAmount ?? 0);
  const paidAmount = Number(invoice?.paidAmount ?? 0);
  const trueRemaining = totalAmount - minimumPartialAmt;
  const amountToPay = partialPaid ? trueRemaining : minimumPartialAmt;
  const amountDueValue = invoice.totalAmountDue || invoice.amountDue || 0;
  const isPaid = Number(amountDueValue) === 0;

  return {
    isPartial: true,
    partialPaid,
    partialMode,
    partialValue,
    totalAmount,
    minimumPartialAmt,
    trueRemaining,
    paidAmount,
    amountToPay,
    fullyPaid: isPaid,
  };
};

// ─── Sub-components ────────────────────────────────────────────────────────────
const StatusBadge = ({ amountDue }) => {
  const isPaid = Number(amountDue) === 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${isPaid ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6]" : "bg-amber-50 text-amber-700 border-amber-200"}`}
    >
      <span
        className={`size-1.5 rounded-full ${isPaid ? "bg-[#0a7822a6]" : "bg-amber-500"}`}
      />
      {isPaid ? "Paid" : "Due"}
    </span>
  );
};

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Main Component ────────────────────────────────────────────────────────────
export default function QuickBookInvoiceDetail() {
  const { id } = useParams();
  const history = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const triggerDate = searchParams.get("date");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const fetchInvoice = useCallback(
    async (invoiceId) => {
      setPageLoading(true);
      loader(true);
      try {
        const res = await ApiClient.get(`quickbooks-docs/invoices/list`, {
          id: invoiceId,
          limit: 1,
        }).catch(() => null);

        if (res?.success) {
          const found = (res?.data || []).find(
            (inv) =>
              inv._id === invoiceId ||
              inv.id === invoiceId ||
              inv.invoiceId?.toString() === invoiceId ||
              inv.qbId === invoiceId,
          );
          if (found) {
            setSelectedInvoice(found);
          } else {
            swal({
              title: "Invoice Not Found",
              text: "The requested invoice could not be found.",
              icon: "error",
              button: "Close",
            }).then(() => history("/order/management?tab=invoice"));
          }
        } else {
          setSelectedInvoice(null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
        loader(false);
      }
    },
    [history],
  );

  useEffect(() => {
    if (id) fetchInvoice(id);
  }, [id, fetchInvoice, triggerDate]);

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-y-3 gap-x-5 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => history("/order/management?tab=invoice")}
              className="size-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <div>
              <h2 className="text-gray-900 text-xl font-semibold">
                Invoice Details
              </h2>
              <p className="text-gray-500 text-sm">
                View full details of this invoice
              </p>
            </div>
          </div>
          <div className="max-sm:w-full">
            {/* <button className="w-full sm:w-fit inline-flex justify-center items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5">Send Invoice</button> */}
          </div>
        </div>

        <div className="max-w-[900px] mx-auto">
          <div className="w-full border rounded-2xl bg-white overflow-hidden">
            {pageLoading ? (
              <div className="flex items-center justify-center py-20">
                {/* <div className="size-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" /> */}
              </div>
            ) : !selectedInvoice ? (
              <div className="flex items-center justify-center py-20">
                <p className="text-gray-500 text-sm">Invoice not found.</p>
              </div>
            ) : (
              (() => {
                const amountDueRaw = Number(
                  selectedInvoice?.totalAmountDue ??
                    selectedInvoice?.amountDue ??
                    0,
                );
                const isPaid = amountDueRaw === 0;
                const p = getPartialInfo(selectedInvoice);

                const invoiceLines = selectedInvoice?.raw?.Line || [];
                const salesLines = invoiceLines.filter(
                  (l) => l.DetailType === "SalesItemLineDetail",
                );
                const discountLine = invoiceLines.find(
                  (item) => item?.DetailType === "DiscountLineDetail",
                );
                const discount = Number(
                  discountLine?.Amount || discountLine?.LineAmount || 0,
                );
                const subtotal = salesLines.reduce(
                  (s, l) => s + Number(l.Amount || 0),
                  0,
                );
                const tax = Number(selectedInvoice?.totalTax || 0);
                const totalInvoice = Number(
                  selectedInvoice?.totalAmount ||
                    selectedInvoice?.raw?.TotalAmt ||
                    subtotal + tax,
                );

                const displayLines =
                  salesLines.length > 0
                    ? salesLines
                    : selectedInvoice?.estimate?.originalLineItems ||
                      selectedInvoice?.lineItems ||
                      [];

                const billAddr =
                  selectedInvoice?.raw?.BillAddr ||
                  selectedInvoice?.BillAddr ||
                  selectedInvoice?.billingAddress;
                const shipAddr =
                  selectedInvoice?.raw?.ShipAddr ||
                  selectedInvoice?.ShipAddr ||
                  selectedInvoice?.shippingAddress;

                return (
                  <>
                    {/* Header */}
                    <div className="px-6 pt-6 pb-5 text-center border-b border-gray-100">
                      <div className="w-12 h-12 bg-[#115B9D] rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold tracking-wide">
                        Invoice Details
                      </h2>
                      <p className="mt-1 text-xs font-semibold tracking-[2px] text-[#115B9D] uppercase">
                        #{selectedInvoice?.invoiceNumber}
                      </p>
                    </div>

                    {/* Customer */}
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-3">
                        Customer
                      </p>
                      <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                        <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                          <span className="text-sm font-bold text-[#115B9D]">
                            {getInitials(
                              selectedInvoice.customerName ||
                                selectedInvoice.contact?.Name,
                            )}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800 capitalize">
                            {selectedInvoice.customerName ||
                              selectedInvoice.contact?.Name ||
                              "—"}
                          </p>
                          {(selectedInvoice.contact?.Email ||
                            selectedInvoice.raw?.BillEmail?.Address) && (
                            <p className="text-xs text-gray-500">
                              {selectedInvoice.contact?.Email ||
                                selectedInvoice.raw?.BillEmail?.Address}
                            </p>
                          )}
                          {(selectedInvoice.contact?.Mobile ||
                            selectedInvoice.contact?.Phone) && (
                            <p className="text-xs text-gray-600">
                              {selectedInvoice.contact?.Mobile ||
                                selectedInvoice.contact?.Phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dates & status bar */}
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-3">
                        Invoice Info
                      </p>
                      <div className="border border-gray-200 rounded-xl grid grid-cols-2 sm:grid-cols-4 items-stretch bg-[#115B9D08]">
                        {[
                          {
                            label: "Created",
                            value: formatDate(
                              selectedInvoice.createdAtQb ||
                                selectedInvoice.createTime,
                            ),
                            color: "text-gray-800",
                          },
                          {
                            label: "Due Date",
                            value: formatDate(selectedInvoice.dueDate),
                            color: "text-red-500",
                          },
                          {
                            label: "Ship Date",
                            value: formatDate(selectedInvoice.shipDate),
                            color: isShipDatePassed(selectedInvoice.shipDate)
                              ? "text-red-500 font-bold"
                              : "text-gray-800",
                          },
                        ].map((item) => (
                          <div
                            key={item.label}
                            className="py-4 px-3 border-r border-gray-200"
                          >
                            <p className="text-xs font-semibold tracking-widest text-gray-600 uppercase mb-1">
                              {item.label}
                            </p>
                            <p className={`text-sm font-bold ${item.color}`}>
                              {item.value}
                            </p>
                          </div>
                        ))}
                        <div className="py-4 px-3 flex flex-col items-center justify-center gap-1">
                          <p className="text-xs font-semibold tracking-widest text-gray-600 uppercase mb-1">
                            Status
                          </p>
                          <StatusBadge amountDue={amountDueRaw} />
                        </div>
                      </div>
                    </div>

                    {/* Partial payment schedule */}
                    {p.isPartial && (
                      <div className="px-5 py-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                            Payment Schedule
                          </p>
                          <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                            {p.partialMode === "percentage"
                              ? `${p.partialValue}% upfront`
                              : "Fixed upfront"}
                          </span>
                        </div>

                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>Payment progress</span>
                            <span>
                              {p.fullyPaid
                                ? "Fully paid"
                                : p.partialPaid
                                  ? "Step 1 complete"
                                  : "Awaiting Step 1"}
                            </span>
                          </div>
                          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
                              style={{
                                width: `${
                                  p.fullyPaid
                                    ? 100
                                    : p.partialPaid
                                      ? Math.round(
                                          (p.minimumPartialAmt /
                                            p.totalAmount) *
                                            100,
                                        )
                                      : 0
                                }%`,
                              }}
                            />
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                            <span>
                              {formatCurrency(
                                p.fullyPaid
                                  ? p.totalAmount
                                  : p.partialPaid
                                    ? p.minimumPartialAmt
                                    : 0,
                              )}{" "}
                              paid
                            </span>
                            <span>{formatCurrency(p.totalAmount)} total</span>
                          </div>
                        </div>

                        {/* Step 1 */}
                        <div
                          className={`flex items-start gap-3 p-3.5 rounded-t-xl border border-b-0 ${
                            p.partialPaid
                              ? "bg-[#F0FEF4] border-green-200"
                              : "bg-amber-50 border-amber-200"
                          }`}
                        >
                          <div
                            className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                              p.partialPaid ? "bg-[#008236]" : "bg-amber-500"
                            }`}
                          >
                            {p.partialPaid ? <CheckIcon /> : "1"}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <p
                                className={`text-sm font-semibold ${
                                  p.partialPaid
                                    ? "text-[#008236]"
                                    : "text-amber-700"
                                }`}
                              >
                                {p.partialPaid
                                  ? "Step 1 — Partial received ✓"
                                  : "Step 1 — Partial payment (pending)"}
                              </p>
                              <p
                                className={`text-sm font-bold ${
                                  p.partialPaid
                                    ? "text-[#008236]"
                                    : "text-amber-700"
                                }`}
                              >
                                {formatCurrency(p.minimumPartialAmt)}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {p.partialMode === "percentage"
                                ? `${p.partialValue}% of total invoice ${formatCurrency(p.totalAmount)}`
                                : "Fixed partial amount as configured"}
                            </p>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div
                          className={`flex items-start gap-3 p-3.5 rounded-b-xl border ${
                            p.fullyPaid
                              ? "bg-[#F0FEF4] border-green-200"
                              : p.partialPaid
                                ? "bg-[#EFF6FF] border-blue-200"
                                : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div
                            className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                              p.fullyPaid
                                ? "bg-[#008236]"
                                : p.partialPaid
                                  ? "bg-[#115B9D]"
                                  : "bg-gray-300"
                            }`}
                          >
                            {p.fullyPaid ? <CheckIcon /> : "2"}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <p
                                className={`text-sm font-semibold ${
                                  p.fullyPaid
                                    ? "text-[#008236]"
                                    : p.partialPaid
                                      ? "text-[#115B9D]"
                                      : "text-gray-600"
                                }`}
                              >
                                {p.fullyPaid
                                  ? "Step 2 — Remaining balance paid ✓"
                                  : p.partialPaid
                                    ? "Step 2 — Remaining balance (due)"
                                    : "Step 2 — Remaining balance (locked)"}
                              </p>
                              <p
                                className={`text-sm font-bold ${
                                  p.fullyPaid
                                    ? "text-[#008236]"
                                    : p.partialPaid
                                      ? "text-[#115B9D]"
                                      : "text-gray-600"
                                }`}
                              >
                                {formatCurrency(p.trueRemaining)}
                              </p>
                            </div>
                            <p
                              className={`text-xs mt-0.5 ${
                                p.fullyPaid
                                  ? "text-[#008236]"
                                  : p.partialPaid
                                    ? "text-gray-500"
                                    : "text-gray-600"
                              }`}
                            >
                              {p.fullyPaid
                                ? "Invoice fully cleared. No balance remaining."
                                : p.partialPaid
                                  ? "Customer owes this to fully clear the invoice."
                                  : "Unlocks once Step 1 is paid."}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Invoice items */}
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2.5">
                        Invoice Items
                      </p>
                      <div className="border border-gray-200 rounded-xl overflow-auto">
                        <table className="w-full text-sm min-w-[500px]">
                          <thead className="bg-[#115B9D]">
                            <tr>
                              {[
                                "#",
                                "Description",
                                "Qty",
                                "Unit Price",
                                "Amount",
                              ].map((h, i) => (
                                <th
                                  key={h}
                                  className={`py-2.5 px-4 text-xs font-semibold whitespace-nowrap text-white tracking-wide ${i === 0 ? "text-left" : i < 2 ? "text-left" : "text-right"}`}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {displayLines.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="py-4 px-4 text-center text-sm text-gray-600"
                                >
                                  No items
                                </td>
                              </tr>
                            ) : (
                              displayLines.map((li, i) => (
                                <tr
                                  key={i}
                                  className="hover:bg-gray-50/60 transition-colors"
                                >
                                  <td className="py-3 px-4 text-gray-600 w-8">
                                    {i + 1}
                                  </td>
                                  <td className="py-3 px-4 text-gray-700">
                                    {li.Description ||
                                      li.description ||
                                      li.SalesItemLineDetail?.ItemRef?.name ||
                                      "—"}
                                  </td>
                                  <td className="py-3 px-4 text-right text-gray-600">
                                    {li.SalesItemLineDetail?.Qty ??
                                      li.qty ??
                                      "—"}
                                  </td>
                                  <td className="py-3 px-4 text-right text-gray-600">
                                    {formatCurrency(
                                      li.SalesItemLineDetail?.UnitPrice ??
                                        li.unitPrice,
                                      selectedInvoice.currencyCode,
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-right font-semibold text-gray-800">
                                    {formatCurrency(
                                      li.Amount ?? li.LineAmount ?? li.amount,
                                      selectedInvoice.currencyCode,
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2.5">
                        Financial Summary
                      </p>
                      <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-100">
                        <div className="flex items-center justify-between px-4 py-3">
                          <p className="text-sm text-gray-500">Subtotal</p>
                          <p className="text-sm font-semibold text-gray-700">
                            {formatCurrency(
                              subtotal,
                              selectedInvoice?.currencyCode,
                            )}
                          </p>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3">
                          <p className="text-sm text-gray-500">
                            Tax (
                            {selectedInvoice?.raw?.TxnTaxDetail?.TaxLine?.[0]
                              ?.TaxLineDetail?.TaxPercent ?? ""}
                            %)
                          </p>
                          <p className="text-sm font-semibold text-gray-700">
                            {formatCurrency(tax, selectedInvoice?.currencyCode)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3">
                          <p className="text-sm text-gray-500">Discount</p>
                          <p className="text-sm font-semibold text-gray-700">
                            -
                            {formatCurrency(
                              discount,
                              selectedInvoice?.currencyCode,
                            )}
                          </p>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3">
                          <p className="text-sm text-gray-500">Invoice Total</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {formatCurrency(
                              totalInvoice,
                              selectedInvoice?.currencyCode,
                            )}
                          </p>
                        </div>

                        {p.isPartial && !isPaid && (
                          <>
                            <div className="flex items-center justify-between px-4 py-3 bg-amber-50">
                              <div>
                                <p className="text-sm text-amber-700 font-medium">
                                  {p.partialPaid
                                    ? "Step 1 received"
                                    : `Step 1 — Due now (${p.partialMode === "percentage" ? `${p.partialValue}%` : "fixed"})`}
                                </p>
                                <p className="text-xs text-amber-600 mt-0.5">
                                  {p.partialPaid
                                    ? "Already collected"
                                    : "Awaiting customer payment"}
                                </p>
                              </div>
                              <p className="text-sm font-bold text-amber-700">
                                {formatCurrency(
                                  p.minimumPartialAmt,
                                  selectedInvoice?.currencyCode,
                                )}
                              </p>
                            </div>
                            <div className="flex items-center justify-between px-4 py-3 bg-blue-50/50">
                              <div>
                                <p
                                  className={`text-sm font-medium ${p.partialPaid ? "text-[#115B9D]" : "text-gray-600"}`}
                                >
                                  {p.partialPaid
                                    ? "Step 2 — Balance still owed"
                                    : "Step 2 — Remaining (locked)"}
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  {p.partialPaid
                                    ? "Customer must pay to clear invoice"
                                    : "Unlocks after Step 1"}
                                </p>
                              </div>
                              <p
                                className={`text-sm font-bold ${p.partialPaid ? "text-[#115B9D]" : "text-gray-600"}`}
                              >
                                {formatCurrency(
                                  p.trueRemaining,
                                  selectedInvoice?.currencyCode,
                                )}
                              </p>
                            </div>
                          </>
                        )}

                        {isPaid && (
                          <div className="flex items-center justify-between px-4 py-3 bg-[#F0FEF4]">
                            <p className="text-sm text-[#008236] font-medium">
                              Total Collected
                            </p>
                            <p className="text-sm font-bold text-[#008236]">
                              {formatCurrency(
                                totalInvoice,
                                selectedInvoice?.currencyCode,
                              )}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between px-4 py-3.5 bg-[#115B9D] rounded-b-xl">
                          <p className="text-sm font-bold text-white tracking-wide">
                            {isPaid
                              ? "Fully Paid ✓"
                              : p.isPartial
                                ? p.partialPaid
                                  ? "Balance Due (Step 2)"
                                  : "Due Now (Step 1)"
                                : "Total Due"}
                          </p>
                          <p className="text-base font-bold text-white">
                            {isPaid
                              ? formatCurrency(
                                  totalInvoice,
                                  selectedInvoice?.currencyCode,
                                )
                              : formatCurrency(
                                  p.isPartial ? p.amountToPay : amountDueRaw,
                                  selectedInvoice?.currencyCode,
                                )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Markup info */}
                    {selectedInvoice?.markup?.type && (
                      <div className="px-5 py-4 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2.5">
                          Markup
                        </p>
                        <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center justify-between">
                          <p className="text-sm text-gray-600 capitalize">
                            {selectedInvoice.markup.type} markup
                          </p>
                          <p className="text-sm font-semibold text-gray-800">
                            {selectedInvoice.markup.type === "percentage"
                              ? `${selectedInvoice.markup.value}%`
                              : formatCurrency(selectedInvoice.markup.value)}
                            {selectedInvoice.markup.amount
                              ? ` (${formatCurrency(selectedInvoice.markup.amount)})`
                              : ""}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Addresses */}
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-3">
                        Addresses
                      </p>
                      <div className="flex gap-4 flex-wrap sm:flex-nowrap">
                        {[
                          { title: "📍 Billing", addr: billAddr },
                          { title: "🚚 Shipping", addr: shipAddr },
                        ].map(({ title, addr }) => (
                          <div
                            key={title}
                            className="flex-1 min-w-[180px] bg-white border border-[#115B9D]/20 p-4 rounded-xl"
                          >
                            <h2 className="text-xs font-semibold text-[#115B9D] border-b border-[#115B9D]/20 pb-2 mb-2 uppercase tracking-wide">
                              {title}
                            </h2>
                            <div className="space-y-0.5 text-sm text-gray-600">
                              {[
                                addr?.Line1,
                                addr?.City || addr?.Line2,
                                addr?.State,
                                addr?.Country || addr?.Line3,
                                addr?.PostalCode,
                              ]
                                .filter(Boolean)
                                .map((line, i) => (
                                  <p key={i}>{line}</p>
                                ))}
                              {!addr?.Line1 && (
                                <p className="text-gray-600 text-xs italic">
                                  No address on file
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Private note */}
                    {selectedInvoice?.raw?.PrivateNote && (
                      <div className="px-5 py-4 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2">
                          Private Note / Tracking
                        </p>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800 font-mono">
                          {selectedInvoice.raw.PrivateNote}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="bg-[#115B9D] px-5 py-3.5">
                      <p className="text-center text-[11px] text-white">
                        © 2026 · All rights reserved · Admin View
                      </p>
                    </div>
                  </>
                );
              })()
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
